import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Send, Users, BellOff, Bell, EyeOff, Eye, Pin, X as XIcon } from "lucide-react";

export default function Community() {
  const { user } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [pinned, setPinned] = useState(null);
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const [showHidden, setShowHidden] = useState(false);
  const endRef = useRef(null);

  const loadRooms = async () => {
    try {
      const { data } = await api.get("/community/rooms");
      setRooms(data || []);
      if (!active && data && data.length) {
        // Prefer staff if user is admin/mod/companion/agency
        const staff = data.find((r) => r.slug === "staff");
        setActive(staff ? staff.slug : data[0].slug);
      }
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
  };

  useEffect(() => { loadRooms(); }, []);

  const loadMessages = async () => {
    if (!active) return;
    try {
      const { data } = await api.get(`/community/${active}/messages`);
      // New shape: { messages, pinned } — old rooms may still return array
      if (Array.isArray(data)) {
        setMessages(data);
        setPinned(null);
      } else {
        setMessages(data.messages || []);
        setPinned(data.pinned || null);
      }
      // Refresh rooms list to clear unread badge
      loadRooms();
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
  };

  useEffect(() => { loadMessages(); /* eslint-disable-next-line */ }, [active]);

  useEffect(() => {
    if (!active) return;
    const t = setInterval(loadMessages, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [active]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !active) return;
    try {
      await api.post(`/community/${active}/messages`, { body: text });
      setText("");
      loadMessages();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const toggleMute = async (room) => {
    await api.post(`/community/${room.slug}/state`, { muted: !room.muted });
    loadRooms();
  };

  const toggleHide = async (room) => {
    await api.post(`/community/${room.slug}/state`, { hidden: !room.hidden });
    if (!room.hidden && room.slug === active) {
      // Just hid the active one; switch to first visible
      const next = rooms.find((r) => r.slug !== room.slug && !r.hidden);
      setActive(next ? next.slug : null);
    }
    loadRooms();
  };

  const pinMessage = async (msgId) => {
    try {
      await api.post(`/community/${active}/pin`, { message_id: msgId });
      loadMessages();
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const unpin = async () => {
    try {
      await api.delete(`/community/${active}/pin`);
      setPinned(null);
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
  };

  if (!user) return null;

  if (!rooms.length) {
    return (
      <div className="max-w-2xl mx-auto px-6 pt-16 pb-24 text-center" data-testid="community-denied">
        <Users size={48} color="var(--brand-primary)" strokeWidth={1} className="mx-auto mb-4" />
        <h1 className="font-serif text-4xl mb-3">Chat comunitario</h1>
        <p style={{ color: "var(--text-secondary)" }}>
          No tienes salas disponibles.
        </p>
      </div>
    );
  }

  const visibleRooms = showHidden ? rooms : rooms.filter((r) => !r.hidden || r.unread > 0);
  const hiddenCount = rooms.filter((r) => r.hidden).length;
  const activeRoom = rooms.find((r) => r.slug === active);
  const isStaff = activeRoom?.slug === "staff";
  const canPin = isStaff && (user.role === "admin" || user.role === "moderator");

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-12 pt-16 pb-24" data-testid="community-page">
      <div className="overline mb-3">Comunidad</div>
      <h1 className="font-serif text-5xl font-light mb-3" style={{ color: "var(--text-primary)" }}>
        Chat <em style={{ color: "var(--brand-primary)", fontStyle: "italic" }}>privado</em>
      </h1>
      <p className="text-sm mb-6" style={{ color: "var(--text-secondary)" }}>
        Salas privadas según tu rol.
        {hiddenCount > 0 && (
          <button
            onClick={() => setShowHidden((v) => !v)}
            className="ml-3 text-xs underline"
            style={{ color: "var(--text-muted)" }}
            data-testid="community-toggle-hidden"
          >
            {showHidden ? "Ocultar archivados" : `Mostrar ${hiddenCount} archivado${hiddenCount === 1 ? "" : "s"}`}
          </button>
        )}
      </p>

      <div className="flex gap-2 mb-3 overflow-x-auto" style={{ borderBottom: "1px solid var(--border-subtle)" }} data-testid="community-rooms">
        {visibleRooms.map((r) => (
          <button
            key={r.slug}
            onClick={() => setActive(r.slug)}
            className="relative px-5 py-3 text-xs uppercase tracking-[0.2em] whitespace-nowrap inline-flex items-center gap-2"
            data-testid={`room-${r.slug}`}
            style={{
              color: active === r.slug ? "var(--brand-primary)" : "var(--text-secondary)",
              borderBottom: `2px solid ${active === r.slug ? "var(--brand-primary)" : "transparent"}`,
              opacity: r.hidden ? 0.55 : 1,
            }}
          >
            {r.slug === "staff" && <span style={{ color: "var(--vip-gold)" }}>★</span>}
            {r.name}
            {r.unread > 0 && (
              <span
                className="inline-flex items-center justify-center rounded-full text-[10px] font-bold"
                style={{
                  minWidth: 18, height: 18, padding: "0 5px",
                  background: "var(--brand-primary)", color: "#fff",
                }}
                data-testid={`room-unread-${r.slug}`}
              >
                {r.unread > 99 ? "99+" : r.unread}
              </span>
            )}
            {r.muted && <BellOff size={12} strokeWidth={1.4} style={{ color: "var(--text-muted)" }} />}
          </button>
        ))}
      </div>

      {activeRoom && (
        <div className="flex items-start justify-between mb-5 gap-4 flex-wrap">
          <p className="text-xs flex-1" style={{ color: "var(--text-muted)" }} data-testid="community-room-desc">
            {activeRoom.description}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => toggleMute(activeRoom)}
              className="text-xs inline-flex items-center gap-1 px-3 py-2"
              style={{
                border: "1px solid var(--border-subtle)",
                color: activeRoom.muted ? "var(--brand-primary)" : "var(--text-secondary)",
              }}
              data-testid="community-mute-toggle"
              title={activeRoom.muted ? "Des-silenciar" : "Silenciar notificaciones"}
            >
              {activeRoom.muted ? <BellOff size={12} /> : <Bell size={12} />}
              {activeRoom.muted ? "Silenciado" : "Silenciar"}
            </button>
            <button
              onClick={() => toggleHide(activeRoom)}
              className="text-xs inline-flex items-center gap-1 px-3 py-2"
              style={{
                border: "1px solid var(--border-subtle)",
                color: activeRoom.hidden ? "var(--brand-primary)" : "var(--text-secondary)",
              }}
              data-testid="community-hide-toggle"
              title={activeRoom.hidden ? "Mostrar" : "Ocultar de la lista"}
            >
              {activeRoom.hidden ? <Eye size={12} /> : <EyeOff size={12} />}
              {activeRoom.hidden ? "Mostrado" : "Ocultar"}
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-col" style={{ height: "60vh", background: "var(--bg-surface)", border: "1px solid var(--border-subtle)" }}>
        {pinned && (
          <div
            className="flex items-start gap-3 p-3"
            style={{
              background: "rgba(212,175,55,0.1)",
              borderBottom: "1px solid var(--vip-gold)",
            }}
            data-testid="community-pinned"
          >
            <Pin size={14} strokeWidth={1.5} style={{ color: "var(--vip-gold)", marginTop: 2 }} />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: "var(--vip-gold)" }}>
                Anuncio fijado por {pinned.author_name}
              </div>
              <p className="text-sm" style={{ color: "var(--text-primary)" }}>{pinned.body}</p>
            </div>
            {canPin && (
              <button onClick={unpin} className="p-1" style={{ color: "var(--text-muted)" }} data-testid="community-unpin">
                <XIcon size={14} />
              </button>
            )}
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-3" data-testid="community-messages">
          {messages.length === 0 ? (
            <p className="text-center text-sm mt-8" style={{ color: "var(--text-muted)" }}>
              Aún no hay mensajes. ¡Sé el primero en escribir!
            </p>
          ) : messages.map((m) => {
            const mine = m.author_user_id === user.id;
            return (
              <div key={m.id} className={`group flex flex-col ${mine ? "items-end" : "items-start"}`}>
                <div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>
                  {m.author_name}
                  {m.author_role === "admin" && <span className="ml-1 font-bold" style={{ color: "var(--vip-gold)" }}>ADMIN</span>}
                  {m.author_role === "moderator" && <span className="ml-1 font-bold" style={{ color: "var(--brand-primary)" }}>MOD</span>}
                  {m.author_role === "agency" && <span className="ml-1 font-bold" style={{ color: "var(--text-secondary)" }}>AGENCIA</span>}
                  {m.author_role === "companion" && <span className="ml-1" style={{ color: "var(--text-secondary)" }}>MUSA</span>}
                  <span className="ml-2">{new Date(m.created_at).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}</span>
                </div>
                <div className="relative px-4 py-2 max-w-[75%]" style={{
                  background: mine ? "var(--brand-primary)" : "var(--bg-elevated)",
                  color: mine ? "var(--bg-base)" : "var(--text-primary)",
                  borderRadius: "2px",
                }}>
                  {m.body}
                  {canPin && (
                    <button
                      onClick={() => pinMessage(m.id)}
                      className="absolute -top-2 -right-2 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{ background: "var(--bg-base)", border: "1px solid var(--vip-gold)", color: "var(--vip-gold)" }}
                      title="Fijar este mensaje como anuncio"
                      data-testid={`pin-msg-${m.id}`}
                    >
                      <Pin size={12} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>

        <form onSubmit={send} className="flex gap-2 p-3" style={{ borderTop: "1px solid var(--border-subtle)" }}>
          <input className="input-field flex-1" placeholder="Escribe un mensaje…"
            value={text} onChange={(e) => setText(e.target.value)} data-testid="community-input" />
          <button type="submit" className="btn-primary" disabled={!text.trim()} data-testid="community-send">
            <Send size={14} />
          </button>
        </form>
      </div>

      {error && <p className="text-xs mt-3" style={{ color: "#ef6d6d" }} data-testid="community-error">{error}</p>}
    </div>
  );
}
